if (!customElements.get('gz-google-reviews')) {
  class GzGoogleReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.toggleBtn = this.querySelector('[data-toggle]');
      if (!this.track) return;

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.wantsAutoplay = this.dataset.autoplay === 'true';
      this.interval = (parseInt(this.dataset.interval, 10) || 6) * 1000;
      this.userPaused = false;
      this.hovering = false;
      this.focusing = false;
      this.inView = false;
      this.timer = null;

      if (this.prevBtn) {
        this.prevBtn.hidden = false;
        this.prevBtn.addEventListener('click', () => this.go(-1, true));
      }
      if (this.nextBtn) {
        this.nextBtn.hidden = false;
        this.nextBtn.addEventListener('click', () => this.go(1, true));
      }
      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.syncToggle();
          this.update();
        });
      }

      this.addEventListener('mouseenter', () => { this.hovering = true; this.update(); });
      this.addEventListener('mouseleave', () => { this.hovering = false; this.update(); });
      this.addEventListener('focusin', () => { this.focusing = true; this.update(); });
      this.addEventListener('focusout', () => { this.focusing = false; this.update(); });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(1, true); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(-1, true); }
      });

      this.onVisibility = () => this.update();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onMotion = () => this.syncMotion();
      if (this.reduced.addEventListener) this.reduced.addEventListener('change', this.onMotion);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.update();
        }, { threshold: 0.3 });
        this.observer.observe(this);
      } else {
        this.inView = true;
      }

      this.syncMotion();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.observer) this.observer.disconnect();
      if (this.reduced && this.reduced.removeEventListener) this.reduced.removeEventListener('change', this.onMotion);
    }

    get canAutoplay() {
      return this.wantsAutoplay && !this.reduced.matches;
    }

    syncMotion() {
      if (this.toggleBtn) this.toggleBtn.hidden = !this.canAutoplay;
      this.syncToggle();
      this.update();
    }

    syncToggle() {
      if (!this.toggleBtn) return;
      const paused = this.userPaused;
      this.toggleBtn.dataset.state = paused ? 'paused' : 'playing';
      this.toggleBtn.setAttribute('aria-label', paused ? this.dataset.labelPlay : this.dataset.labelPause);
    }

    shouldRun() {
      return this.canAutoplay && !this.userPaused && !this.hovering && !this.focusing && this.inView && !document.hidden;
    }

    update() {
      const running = this.shouldRun();
      this.track.setAttribute('aria-live', running ? 'off' : 'polite');
      if (running) this.start(); else this.stop();
    }

    start() {
      if (this.timer) return;
      this.timer = window.setInterval(() => this.go(1, false), this.interval);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    slides() {
      return Array.from(this.track.children);
    }

    currentIndex() {
      const slides = this.slides();
      const base = slides[0].offsetLeft;
      const pos = this.track.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        const dist = Math.abs(slide.offsetLeft - base - pos);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      return best;
    }

    go(direction, fromUser) {
      const slides = this.slides();
      if (!slides.length) return;
      const base = slides[0].offsetLeft;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const atEnd = this.track.scrollLeft >= max - 2;
      const atStart = this.track.scrollLeft <= 2;
      let target;
      if (direction > 0 && atEnd) {
        target = 0;
      } else if (direction < 0 && atStart) {
        target = max;
      } else {
        const index = Math.min(Math.max(this.currentIndex() + direction, 0), slides.length - 1);
        target = Math.min(slides[index].offsetLeft - base, max);
      }
      const behavior = this.reduced.matches ? 'auto' : 'smooth';
      this.track.scrollTo({ left: target, behavior });
      if (fromUser && this.timer) { this.stop(); this.update(); }
    }
  }
  customElements.define('gz-google-reviews', GzGoogleReviews);
}
